import React, { MouseEvent, ReactNode } from 'react';
import Breadcrumb from '../Breadcrumb';
import CustomEditorItemComponent from '../custom/CustomEditorItemComponent';
import ComponentName from '../../constant/ComponentName';
import RoleHelper from '../../RoleHelper';
import CustomTabv2 from '../custom/CustomTabv2';
import ComponentAuditLog from '../ComponentAuditLog';
import { FormicaProps, withFormica } from '../../helpers/withFormica';
import { RootState } from '../../redux/store';
import { ActionState } from '../../redux/reducers/ActionReducer';
import { changeAction, changeActionDirty, changeActionIsUpdate, clearAction } from '../../redux/actions/ActionActions';
import { clearPage } from '../../redux/actions/PageActions';
import { enterComponent, leaveComponent } from '../../redux/actions/ComponentActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { InstanceContainer } from '../../config/binding/InstanceBinding';
import Injectables from '../../constant/Injectables';
import ActionService from '../../service/ActionService';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import { query, repository, subscribe } from '@trlogic/client-js';
import CustomLoading from '../custom/CustomLoading';
import CollectionNames from '../../constant/CollectionNames';
import { ActionJsonModel } from '../../domain/collection/rule/action/ActionDomains';
import { customConfirm } from '../custom/CustomPopupConfirm';
import { confirmUndeployWithImpact } from '../deploy/UndeployImpactDialog';
import StandardEditorContainerComponent, { StandardEditorContainerComponentArgs } from '../../helpers/StandardEditorContainerComponent';
import ActionAccessComponent from './ActionAccessComponent';
import { isStringValid } from '../../util/Generator';
import MessageUtil from '../../util/MessageUtil';
import LanguageUtil from '../../util/LanguageUtil';
import { EnrichedComponentCategory } from '../../domain/category/CategoryOperationDomains';
import { ConfirmPopup } from 'primereact/confirmpopup';
import eq = query.eq;

class ActionEditorContainer extends StandardEditorContainerComponent<Props, State, ActionJsonModel> {
  public state = new State();

  private actionSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly actionService: ActionService = ServiceContainer.getInstance().get(ServiceNames.ACTION);

  constructor(props: Props) {
    const args: StandardEditorContainerComponentArgs = {
      canPreview: true,
      component: ComponentName.ACTION,
      icon: props.icon,
    };
    super(props, args);
  }

  public render(): ReactNode {
    const {
      action: { action },
      isUpdate,
    } = this.props;
    const { loading } = this.state;

    if (loading) {
      return <CustomLoading />;
    }

    return (
      <div className={'w-full h-full flex flex-col overflow-y-hidden'}>
        <Breadcrumb />
        <ConfirmPopup />
        <CustomEditorItemComponent
          name={action.name}
          description={action.description}
          component={ComponentName.ACTION}
          status={action?.stage ?? 'DRAFT'}
          onSave={this.saveAction}
          onRemove={this.removeAction}
          onDeploy={this.deployAction}
          onUndeploy={this.undeployAction}
          isUpdate={isUpdate}
          icon={this.props.icon}
          onChangeDescription={this.onChangeDescription}
          onChangeName={this.onChangeName}
          disableSave={!RoleHelper.canEdit('Action')}
          disableRemove={!RoleHelper.canEdit('Action')}
          disableNameChange={!RoleHelper.canEdit('Action')}
          disableDescriptionChange={!RoleHelper.canEdit('Action')}
          disableDeploy={!RoleHelper.canDeploy('Action')}
          disableUndeploy={!RoleHelper.canDeploy('Action')}
        />
        <div className={`flex flex-col h-full w-full bg-white rounded`}>
          <CustomTabv2 tab={this.getTabs()} />
        </div>
      </div>
    );
  }

  public async componentWillUnmount() {
    const { clearAction } = this.props;
    clearAction();
    await super.componentWillUnmount();
  }

  protected _startTracker = async () => {
    const {
      changeActionIsUpdate,
      type,
      changeAction,
      action: { action },
    } = this.props;
    const variableId = this.getItemIdFromRoute();
    if (variableId == undefined) {
      action.template = type;
      changeAction(action);
      changeActionIsUpdate(false);
      this.setState({ loading: false });
    }
    const actionQuery = query.QueryBuilder.select().from(CollectionNames.ACTIONS).where(eq('name', variableId));
    this.actionSubscription = subscribe.Formica.subscribe(CollectionNames.ACTIONS, actionQuery.build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.actionSubscription]);
  };

  protected tracker = async () => {
    const { changeActionIsUpdate, changeActionDirty, changeAction } = this.props;
    if (this.actionSubscription?.ready()) {
      const actionId = this.getItemIdFromRoute();
      if (actionId == undefined) {
        changeActionIsUpdate(false);
        changeActionDirty(false);
        RoleHelper.setCurrentAccess('Edit');
        this.setState({ loading: false });
        return;
      }
      const actionQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.ACTIONS).where(eq('name', actionId)).build();
      const action: ActionJsonModel | undefined = await this.repository.find(actionQuery);
      if (action == undefined) return;
      changeAction(action);
      changeActionIsUpdate(true);
      changeActionDirty(false);
      RoleHelper.setCurrentAccess(action);
      this.setState({ loading: false });
    }
  };

  protected _stopTracker = async () => {
    await this.actionSubscription?.stop();
    this.computation?.stop();
  };

  private onChangeName = (name: string) => {
    const {
      action: { action },
    } = this.props;
    if (isStringValid(name)) {
      action.name = name.trim();
      this.changeItem(action);
    } else {
      MessageUtil.warning({
        summary: LanguageUtil.inappropriateCharacters(),
      });
    }
  };

  private onChangeDescription = (description: string) => {
    const {
      action: { action },
    } = this.props;
    action.description = description;
    this.changeItem(action);
  };

  private saveAction = async () => {
    const {
      action: { action },
      changeActionIsUpdate,
      isUpdate,
      lastCategory,
      changeActionDirty,
    } = this.props;
    const result = await this.actionService.save(action, isUpdate, lastCategory);
    if (result) {
      if (!isUpdate) await this.switchToUpdate();
      changeActionIsUpdate(true);
    }
  };

  private deployAction = (e: MouseEvent<HTMLButtonElement>) => {
    const {
      action: { action },
      changeActionDirty,
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        await this.actionService.save(action, true, {} as EnrichedComponentCategory);
        await this.actionService.deploy(action);
      },
      message: LanguageUtil.get({ id: 'messages.actionDeployed', defaultMessage: 'Action Deployed' }),
    });
  };

  private undeployAction = (e: MouseEvent<HTMLButtonElement>) => {
    const {
      action: { action },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: () => confirmUndeployWithImpact(ComponentName.ACTION, [action.name], () => this.actionService.undeploy(action)),
      message: LanguageUtil.get({ id: 'messages.actionUndeployed', defaultMessage: 'Action Undeployed' }),
    });
  };

  private removeAction = (e: MouseEvent<HTMLButtonElement>) => {
    const {
      action: { action },
      router: { navigate },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: () => {
        this.actionService.remove(action);
        navigate('/action');
      },
      message: LanguageUtil.get({ id: 'messages.confirm.removeAction', defaultMessage: 'Are you sure remove action ?' }),
    });
  };

  protected getItemIdFromRoute = () => {
    return this.props.router.params.id;
  };

  private switchToUpdate = async () => {
    const {
      action: { action },
      router: { navigate, params, location },
      enterComponent,
      type,
    } = this.props;
    if (action.name == '') return;
    enterComponent({ component: ComponentName.ACTION, id: action.name });
    navigate(`/action${params.folderId ? `/folder/${params.folderId}` : ''}/${action.template.toLowerCase()}/${action.name}`, { replace: true });
    setTimeout(() => {
      this.computation?.onStop(() => this.startTracker());
      this.stopTracker();
    });
  };

  protected getTabs = () => {
    return [
      {
        key: 'Action',
        label:
          this.props.type?.substring(0, 1)?.toUpperCase() + this.props.type?.substring(1) ||
          LanguageUtil.get({ id: 'common.label.action', defaultMessage: 'Action' }),
        content: this.props.editor,
      },
      {
        key: 'Access',
        label: LanguageUtil.get({ id: 'common.label.access', defaultMessage: 'Access' }),
        content: <ActionAccessComponent />,
        visible: RoleHelper.canManage(ComponentName.ACTION, true),
      },
      {
        key: 'Audit',
        label: LanguageUtil.get({ id: 'common.label.audit', defaultMessage: 'Audit' }),
        content: <ComponentAuditLog component={ComponentName.ACTION} documentName={this.props.action.action.name} canPreview />,
        visible: RoleHelper.canManage(ComponentName.ACTION, true) && this.environmentUtil.isAuditLogVisible(),
      },
    ];
  };

  protected changeItem = (item: ActionJsonModel) => {
    const { changeAction, changeActionDirty } = this.props;
    changeAction(item);
    changeActionDirty(true);
  };
}

class State {
  loading: boolean = true;
}

const mapStateToProps = (state: RootState) => ({
  action: state.action as ActionState,
  isUpdate: state.action.isUpdate,
  lastCategory: state.page.lastCategory,
});
const mapDispatchToProps = {
  changeActionIsUpdate,
  changeAction,
  clearAction,
  enterComponent,
  leaveComponent,
  clearPage,
  changeActionDirty,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

interface Props extends ReduxProps, FormicaProps {
  editor: ReactNode;
  type: string;
  icon: string;
}

export default withFormica(connector(ActionEditorContainer));
